// ----------------------------------------------------------------------------
// Component: Order Sheet
// ----------------------------------------------------------------------------
// There are 2 types of cart submission in order for custom properties to be passed over to the cart.
// Specifically because this is the only way to currently get image uploads to the cart.
// 1. Cart Submission with Decorations - This is for products that have decoration services (especially image uploads)
// 2. Cart Submission without Decorations - This is for products that do not have decoration services and remaining products in the cart from the first type of submission
// See https://www.figma.com/board/CHJnn8xkpXNNbpRmuWiPRI/OTS-B2B-Order-Sheet-Code-Flow for more details
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

import './_order-sheet.scss';

// Scripts
import { initAccordion } from 'snippets/_containers/accordion/accordion';
import {formatMoney} from '@shopify/theme-currency';

// -------------------------------------
//  Variables
// -------------------------------------
const ORDER_SHEET_WRAPPER             = '.order-sheet-wrapper';
const QUANTITY_SWITCHER_COMPONENT     = 'quantity-switcher';
const ERROR_MESSAGE                   = '.order-sheet__error-message';
const ORDER_TOTAL                     = '.order-sheet__product-total';

// -------------------------------------
//  Order Sheet
// -------------------------------------


function fetchConfig(type = 'json') {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
    };
  }

class orderSheet extends HTMLElement {
    constructor() {
        super();

        // Order Sheet Elements - used for both types of Cart Submission


        // Decorations Elements - used for Cart Submission with Decorations

        this.productTitle       = this.dataset.productTitle;
        this.sectionId          = this.id;
        this.sectionWrapper     = this.closest(ORDER_SHEET_WRAPPER);
        this.quantitySwitchers  = this.querySelectorAll(QUANTITY_SWITCHER_COMPONENT);
        this.firstVariantIDEl   = this.querySelector('input[name="id"]');
        this.orderTotal         = this.querySelector(ORDER_TOTAL);
        this.orderTotalPriceEl  = this.orderTotal.querySelector('.product-value-total');
        this.orderTotalQtyEl    = this.orderTotal.querySelector('.product-qty-total');
        this.errorMessage       = this.querySelector(ERROR_MESSAGE);
        this.inputTimer         = null;
        this.timeout            = null;
        this.submitButton       = this.querySelector('#update-cart');
        this.loadingAnimation   = this.querySelector('.loading__spinner');
        this.form               = this.querySelector('form');
        this.forms              = this.querySelectorAll('form');
        this.startingVariantID  = this.querySelector('input[name="id"]').value;
        this.allowsDecoServ     = this.form.dataset.allowsDecorationServices; // returns string so don't use true when you should use 'true'
        this.decorationAccordionButton = this.querySelector('#accordion-decoration--button');
        this.skipButton         = this.querySelector('#skip');
        this.customizeButton    = this.querySelector('#customize');
        this.closedDecorationsAccordionBtn = this.querySelector('#accordion-decoration--button[aria-expanded="false"]');
        this.decorationsInputsContainer =  this.querySelector('#accordion--decoration--content');
        this.decorationControls = this.querySelector('#accordion--decoration-controls');
        this.hasDecorations = false;
        this.skipped = false;
        this.openDecorationsAccordion = false;

        this.quantitySwitchers.forEach((qtySwitch) => {
            qtySwitch.addEventListener('quantityChange', this.trackPricing.bind(this));
        });

        if (this.allowsDecoServ === 'true') {
            this.skipButton.addEventListener('click', () => {
                this.skipped = true;
                if (this.hasQuantity()) {
                    this.submitButton.disabled = false;
                }
            });

            this.customizeButton.addEventListener('click', () => {
                this.decorationsInputsContainer.querySelectorAll('input, textarea').forEach((input) => {
                    input.required = true;
                });
                this.decorationAccordionButton.disabled = false;

                // Open the accordion
                this.closedDecorationsAccordionBtn.click();
                this.decorationControls.style.display = 'none';
                this.openedDecorationsAccordionBtn = this.querySelector('#accordion-decoration--button[aria-expanded="true"]');
                this.openedDecorationsAccordionBtn.disabled = true;
                this.openDecorationsAccordion = true;

                if (this.hasQuantity()) {
                    this.submitButton.disabled = false;
                }
            });

        } else {
            this.submitButton.disabled = false;
        }

        this.form.addEventListener('submit', this.chooseSubmission.bind(this));

    }

    hasQuantity() {
        const quantityInputs = this.querySelectorAll('.order-sheet__quantity-input');
        let quantity = 0;
        quantityInputs.forEach(input => {
            const variantQty = parseFloat(input.value);
            quantity += variantQty;
        });
        return quantity > 0;
    }

    trackVariantPricing(event) {

        const quantityInput = event.target;
        const variantContainer = quantityInput.closest('.variant-container');
        const variantTitleQuantityEl = variantContainer.querySelector('[data-variant-quantity]');
        const variantTitlePriceEl = variantContainer.querySelector('[data-variant-price]');
        const variantQuantityInputs = variantContainer.querySelectorAll('.order-sheet__quantity-input');

        let total = 0;
        let quantity = 0;
        variantQuantityInputs.forEach(input => {
            const variantPriceEl = input.closest('.order-sheet__row--variant').querySelector('.order-sheet__variant-total');
            const variantPrice = parseFloat(input.dataset.price);
            const variantQty = parseFloat(input.value);
            const variantTotal = variantPrice * variantQty;

            // Update variant price
            variantPriceEl.innerHTML = formatMoney(variantTotal, theme.moneyFormat);


            // Update total
            total += variantTotal;
            quantity += variantQty;
        });

        variantTitlePriceEl.innerHTML = formatMoney(total, theme.moneyFormat);
        variantTitleQuantityEl.innerHTML = `(${quantity} item${quantity !== 1 ? 's' : ''})`;

    }

    trackPricing(event) {
        this.trackVariantPricing(event);

        // TODO: Refactor this to be more efficient and potentially update totals during trackVariantPricing instead of here


        // Get quantity multiplied by price for each variant and sum them
        const quantityInputs = this.querySelectorAll('.order-sheet__quantity-input');
        let total = 0;
        let quantity = 0;
        quantityInputs.forEach(input => {
            const variantPriceEl = input.closest('.order-sheet__row--variant').querySelector('.order-sheet__variant-total');
            const variantPrice = parseFloat(input.dataset.price);
            const variantQty = parseFloat(input.value);
            const variantTotal = variantPrice * variantQty;

            // Update variant price
            variantPriceEl.innerHTML = formatMoney(variantTotal, theme.moneyFormat);


            // Update total
            total += variantTotal;
            quantity += variantQty;
        });

        // Update order total
        this.orderTotalPriceEl.innerHTML = formatMoney(total, theme.moneyFormat);
        this.orderTotalQtyEl.innerHTML = `(${quantity} item${quantity !== 1 ? 's' : ''})`;

        if (this.skipped || this.openDecorationsAccordion) {
            if (this.hasQuantity()) {
                this.submitButton.disabled = false;
            }
        }

    }

    showLoading = () => {
        this.submitButton.classList.add('loading');
        this.loadingAnimation.classList.remove('hidden');
    }

    hideLoading = () => {
        this.submitButton.classList.remove('loading');
        this.loadingAnimation.classList.add('hidden');
    }


    chooseSubmission(e) {
        e.preventDefault();
        this.showLoading();
        if (this.allowsDecoServ === 'true') {
            this.submitCartWithDecorations(e);
        } else {
            this.submitCart(e, this.startingVariantID);
        }
    }


    submitCart(e, variantWithFile) {
        e.preventDefault();

        // Once file upload is added to the first variant, we can submit the rest of the cart

        // Fetch the current cart state
        fetch(window.Shopify.routes.root + 'cart.js', {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            }
        })
        .then(response => {
            if (!response.ok) {
                throw new Error('Network response was not ok ' + response.statusText);
            }
            return response.json();
        })
        .then(cart => {
            // Construct the items array for adding new items with custom properties
            const itemsToAdd = [];

            // Construct the updates object for updating quantities
            const updates = {};

            // Select all quantity input elements
            const quantityInputs = this.form.querySelectorAll('.order-sheet__quantity-input');

            // Loop through each quantity input and construct the items array and updates object
            quantityInputs.forEach(input => {
                const variantId = parseInt(input.getAttribute('data-variant-id'));
                const quantity = parseInt(input.value);

                if (!isNaN(quantity)) {
                    // Check if the item already exists in the cart
                    const existingItem = cart.items.find(item => item.variant_id === variantId);

                    if (existingItem) {
                        // Update the quantity of the existing item
                        updates[existingItem.key] = quantity;
                    } else if (quantity > 0) {
                        // Add new item with custom properties
                        const item = {
                            id: variantId,
                            quantity: quantity
                        };

                        if (this.hasDecorations === true) {
                            // Append the decoration service as a custom property to the item
                            // TODO: Update if more custom properties are added. File upload is handled in the submitCartWithDecorations function
                            const decoType = this.form.querySelector('input[name="properties[Decoration Type]"]:checked');
                            const designColors = this.form.querySelector('input[name="properties[Design Colors]"]:checked');
                            const additionalDetails = this.form.querySelector('textarea[name="properties[Additional Details]"]');
                            item.properties = {
                                'Decoration Type': decoType.value,
                                'Design Colors': designColors.value,
                                'File Upload': `File Uploaded with ${this.productTitle}`,
                                'Additional Details': additionalDetails.value
                            };
                        }

                        itemsToAdd.push(item);
                    }
                }
            });

            // Function to update quantities
            const updateQuantities = async () => {
                if (Object.keys(updates).length > 0) {
                    // Create a new FormData object for updates
                    let formData = new FormData();

                    // Append the updates object to formData
                    for (const [key, quantity] of Object.entries(updates)) {
                        formData.append(`updates[${key}]`, quantity);
                    }

                    // Send the formData to Shopify's cart API
                    const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
                        method: 'POST',
                        body: formData,
                        headers: {
                            'Accept': 'application/json',
                            'X-Requested-With': 'XMLHttpRequest'
                        }
                    });
                    if (!response.ok) {
                        throw new Error('Network response was not ok ' + response.statusText);
                    }
                    return await response.json();
                } else {
                    return Promise.resolve();
                }
            };

            // Function to add items with custom properties
            const addItems = async () => {
                if (itemsToAdd.length > 0) {
                    const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
                        method: 'POST',
                        body: JSON.stringify({ items: itemsToAdd }),
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept': 'application/json',
                            'X-Requested-With': 'XMLHttpRequest'
                        }
                    });
                    if (!response.ok) {
                        throw new Error('Network response was not ok ' + response.statusText);
                    }
                    return await response.json();
                } else {
                    return Promise.resolve();
                }
            };

            // First add items with custom properties, then update quantities
            addItems()
                .then(updateQuantities)
                .then(data => {
                    // Cart updated -> Update the section
                    this.updateSection();
                    // Temporary cart page redirect while we wait for what the flow should be
                    window.location.href= '/cart';
                })
                .catch(error => {
                    console.error('Error updating cart:', error);
                });
        })
        .catch(error => {
            console.error('Error fetching cart:', error);
        });
    }

    getFormFieldValue(input) {
        let value = null;

        switch (input.type) {
            case 'checkbox':
            case 'radio':
                if (input.checked) {
                    value = input.value;
                }
                break;
            case 'file':
                if (input.files.length > 0) {
                    value = input.files[0]; // Assuming you want the first file
                }
                break;
            case 'select-one':
            case 'select-multiple':
                value = [...input.options].filter(option => option.selected).map(option => option.value);
                break;
            default:
                value = input.value;
                break;
        }

        return value;
    }

    submitCartWithDecorations(e) {
        e.preventDefault();

        const requiredInputs = this.decorationsInputsContainer.querySelectorAll('input[required], select[required], textarea[required]');
        const requiredInputsValues = [...requiredInputs].map(input => ({
            input,
            value: this.getFormFieldValue(input)
        }));

        // Check if any required input is missing a value
        const missingFields = requiredInputsValues.filter(({ input, value }) => {
            if (input.type === 'radio') {
                // For radio inputs, check if any radio button in the group is checked
                const radioGroup = this.decorationsInputsContainer.querySelectorAll(`input[name="${input.name}"]`);
                return ![...radioGroup].some(radio => radio.checked);
            }
            return value === null || value === '';
        });

        if (missingFields.length > 0) {
            // Show error message for missing required fields
            this.hideLoading();
        }

        if (requiredInputs.length > 0) {
            if (missingFields.length == 0) {
                this.hasDecorations = true;
                // Update variant id to make form capabale of passing over a file upload to the first variant with a value over 1
                const valuedSwitchers = [...this.quantitySwitchers].filter(switcher => switcher.inputBox.value > 0);
                const newInputId = valuedSwitchers[0].inputBox.dataset.variantId;
                this.firstVariantIDEl.value = newInputId;


                const config = fetchConfig('javascript');
                config.headers['X-Requested-With'] = 'XMLHttpRequest';
                delete config.headers['Content-Type'];

                const formData = new FormData(this.form);
                config.body = formData;

                fetch(window.Shopify.routes.root + 'cart/add.js', config)
                .then(response => response.json())
                .then(response => {
                    if (response.message) {
                        return console.error(response);
                    }
                    this.submitCart(e, this.firstVariantIDEl.value); // submit cart with decorations
                })
            }
        } else {
            this.submitCart(e, this.startingVariantID); // Submit cart without decorations
        }

    }


    /**
     * Re-render order-sheet section when a change is made.
    */
    updateSection() {
        const sectionId = this.sectionId;

        fetch(window.location.pathname + `?sections=${sectionId}`)
          .then(response => response.json())
          .then(response => {
            if (response.message) {
                return this.showError(response);
            }

            const sectionHTML = response;
            this.sectionWrapper.innerHTML = sectionHTML[`${sectionId}`];
            initAccordion();
          })
          .catch((error) => {
            console.error('Error:', error);
          });
    }

    showError(errorMessage) {
        const errorContent = errorMessage?.description ? errorMessage.description : errorMessage.message;
        this.errorMessage.innerHTML = errorContent;

        if (this.timeout) clearTimeout(this.timeout);

        this.timeout = setTimeout(()=> {
            this.errorMessage.style.transition = 'opacity .5s ease-out';
            this.errorMessage.style.opacity = '0';
        }, 3500);

        this.timeout = setTimeout(()=> {
            this.updateSection();
        }, 4000);
    }
}

customElements.define('order-sheet', orderSheet);

// -------------------------------------
//  Quantity Switcher
// -------------------------------------
class quantitySwitcher extends HTMLElement {
    constructor() {
        super();
        this.quantityContainer = this.querySelector('.order-sheet__quantity-container');
        this.minusBtn          = this.querySelector('.order-sheet__quantity-button--down');
        this.plusBtn           = this.querySelector('.order-sheet__quantity-button--up');
        this.inputBox          = this.querySelector('.order-sheet__quantity-input');

        this.updateButtonStates();

        this.quantityContainer.addEventListener('click', this.handleButtonClick.bind(this));
        this.inputBox.addEventListener('input', this.handleQuantityChange.bind(this));
    }

    updateButtonStates() {
        const value = parseInt(this.inputBox.value);
        this.minusBtn.disabled = value < 1;
    }

    handleButtonClick(event) {
        if (event.target.isSameNode(this.minusBtn)) {
            this.decreaseValue();
        } else if (event.target.isSameNode(this.plusBtn)) {
            this.increaseValue();
        }
    }

    decreaseValue() {
        let value = parseInt(this.inputBox.value);
        value = isNaN(value) ? 1 : Math.max(value - 1, 0);
        this.inputBox.value = value;
        this.updateButtonStates();
        this.handleQuantityChange();
    }

    increaseValue() {
        let value = parseInt(this.inputBox.value);
        value = isNaN(value) ? 1 : (value + 1);
        this.inputBox.value = value;
        this.updateButtonStates();
        this.handleQuantityChange();
    }

    handleQuantityChange() {
        let value = parseInt(this.inputBox.value);
        value = isNaN(value) ? 1 : value;

        const quantityChange = new CustomEvent('quantityChange', {
            detail: {
              qty: value,
              variantId: this.inputBox.dataset.variantId
            },
        });

        this.dispatchEvent(quantityChange);
    }
}

customElements.define('quantity-switcher', quantitySwitcher);

